'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import {
  registerStudent,
  registerCompany,
} from '@eliadmin/core/src/api/jobportal/jobporta-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';

import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';

import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { RegisterFormJobSeeker } from './register-form-jobseeker';
import { Heading } from '../../ui/heading';
import { Paragraph } from '../../ui/paragraph';
import Image from 'next/image';

type RegisterFormJobPortalProps = {
  countries: Country[];
};

export const registerJobportalSchema = (_VM: FormValidationMessages) =>
  z.object({
    // school can be added, form is ready (register-form-school.tsx)
    userType: z.enum(['jobseeker', 'employer']),
  });

export function RegisterFormJobPortal({ countries }: RegisterFormJobPortalProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof registerJobportalSchema>>>({
    resolver: zodResolver(registerJobportalSchema(formValidationMessages)),
    defaultValues: {
      userType: 'jobseeker' as const,
    },
  });

  const { toast } = useToast();
  const router = useRouter();

  const handleRegistration = async (formData: any, userType: 'jobseeker' | 'employer') => {
    try {

      let resp;
      if (userType === 'jobseeker') {
        // Job seeker registration using registerStudent API
        const transformedData = {
          name: formData.name || '',
          surname: formData.surname || '',
          email: formData.register_email,
          phone: formData.phone || '',
          password: formData.password,
          password_confirmation: formData.password_confirmation,
          gdpr: formData.gdpr,
          newsletter: formData.newsletter,
          newsletter_return_url: new URL(
            ROUTES.NEWSLETTER_CONFIRM(),
            env.NEXT_PUBLIC_APP_URL,
          ).toString(),
          tos: formData.tos,

        };
        resp = await registerStudent({ locale, ...transformedData });
      } else {
        // Company registration using registerCompany API
        // The registerCompany API expects the data in a specific format
        const transformedData = {
          name: formData.name || '',
          ico: formData.ico,
          dic: formData.dic,
          ic_dph: formData.ic_dph,
          www: formData.www,
          address: formData.address,
          street_number: formData.street_number,
          postcode: formData.postcode,
          city: formData.city,
          country: formData.country,
          contact_name: formData.contact_name,
          contact_surname: formData.contact_surname,
          title_before_name: formData.title_before_name,
          title_after_name: formData.title_after_name,
          email: formData.email,
          phone: formData.without_phone ? null : formData.phone,
          different_operation_address: formData.different_operation_address,
          operation_address: formData.operation_address,
          operation_street_number: formData.operation_street_number,
          operation_postcode: formData.operation_postcode,
          operation_city: formData.operation_city,
          operation_country: null,
          categories: formData.categories || [],
          tags: formData.tags || [],
          register_email: formData.register_email,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
          gdpr: formData.gdpr,
          newsletter: formData.newsletter,
          newsletter_return_url: new URL(
            ROUTES.NEWSLETTER_CONFIRM(),
            env.NEXT_PUBLIC_APP_URL,
          ).toString(),
          tos: formData.tos,

        };

        resp = await registerCompany({ locale, ...transformedData });
      }

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        throw e; // Re-throw to let child components handle field errors
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
      throw e;
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const userType = form.watch('userType');

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  return (
    <>
      <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
        <section className={cn('custom-section', 'py-12 sm:py-16 lg:py-24')}>
          <div
            className={cn(
              'custom-container',
              'flex flex-col items-start justify-between gap-12 md:flex-row',
            )}
          >
            <div className="flex w-full max-w-[556px] flex-col items-start justify-start gap-8">
              <div className="flex w-full flex-col items-start justify-start gap-3">
                <Heading title={t('text261')} />
                <Paragraph>
                  Už mám účet, chcem sa&nbsp;
                  <Link prefetch={false} href={ROUTES.SIGN_IN()} className="text-primary">
                    prihlásiť.
                  </Link>
                </Paragraph>
              </div>
              <RegisterFormJobSeeker
                onSubmit={handleRegistration}
                showPassword={showPassword}
                showPasswordConfirmation={showPasswordConfirmation}
                onPasswordMouseDown={handlePasswordMouseDown}
                onPasswordMouseUp={handlePasswordMouseUp}
                onPasswordConfirmationMouseDown={handlePasswordConfirmationMouseDown}
                onPasswordConfirmationMouseUp={handlePasswordConfirmationMouseUp}
                countries={countries}
              />
            </div>
            <Image
              src={'/images/register.avif'}
              alt="Register"
              width={0}
              height={0}
              className="hidden h-[400px] w-full max-w-[760px] rounded-[12px] object-cover object-center lg:block"
              sizes="100vw"
            />
          </div>
        </section>
      </Form>
    </>
  );
}
